import stringify from 'json-stable-stringify';
import _ from 'underscore';

import {assert, assertAndContinue} from 'Asserts';
import {TagSchema} from 'constants/RegistryConstants';
import {Status} from 'constants/StatusConstants';
import {
  ContainingSchemaTagType,
  CustomTag,
  isAffiliatedSchemaTag,
  isCustomTag,
  isSchemaFieldDatabaseBacked,
  isSchemaTag,
  MinimalSchemaTag,
  SchemaFieldDataType,
  SchemaTag,
  Tag,
  TagsDict,
  TargetLinkable,
  UnaffiliatedSchemaTag,
} from 'folder-items/FileConstants';
import notifications from 'notifications';
import {FIELD_TYPES, FieldType, FullFieldType, SchemaField} from 'schemas/fields/SchemaFieldConstants';
import * as IntegrationTagUtil from 'schemas/tags/integration/IntegrationTagUtil';
import {formalTime} from 'util/DateUtil';
import pick from 'util/pick';
import {Optional} from 'util/Types';

export interface TagUpdateObject {
  [name: string]: Partial<Tag>;
}

export function getLinksKeyFromFieldType(
  fieldType: FieldType | string
):
  | 'targetAssayRequests'
  | 'targetResults'
  | 'targetAssayRuns'
  | 'targetBatches'
  | 'targetBlobs'
  | 'targetEntries'
  | 'targetFiles'
  | 'targetStorables'
  | 'targetSamples'
  | null {
  switch (fieldType) {
    case FIELD_TYPES.ASSAY_REQUEST_LINK.type:
      return 'targetAssayRequests';
    case FIELD_TYPES.ASSAY_RESULT_LINK.type:
      return 'targetResults';
    case FIELD_TYPES.ASSAY_RUN_LINK.type:
      return 'targetAssayRuns';
    case FIELD_TYPES.BATCH_LINK.type:
      return 'targetBatches';
    case FIELD_TYPES.BLOB_LINK.type:
      return 'targetBlobs';
    case FIELD_TYPES.ENTRY_LINK.type:
      return 'targetEntries';
    case FIELD_TYPES.AA_PART_LINK.type:
    case FIELD_TYPES.BASE_MOLECULE_LINK.type:
    case FIELD_TYPES.BIOENTITY_LINK.type:
    case FIELD_TYPES.PART_LINK.type:
    case FIELD_TYPES.TRANSCRIPTION_LINK.type:
    case FIELD_TYPES.TRANSLATION_LINK.type:
      return 'targetFiles';
    case FIELD_TYPES.STORABLE_LINK.type:
      return 'targetStorables';
    case FIELD_TYPES.SAMPLE_LINK.type:
      return 'targetSamples';
    default:
      return null;
  }
}

function getInitialUnaffiliatedTag(field: SchemaFieldDataType): UnaffiliatedSchemaTag {
  const initialTag: UnaffiliatedSchemaTag = {
    tagSchemaField: field,
    value: '',
    url: '',
    booleanValue: null,
    datetimeValue: null,
    floatValue: null,
    integerValue: null,
    jsonValue: null,
    schemaFieldSelectorOptionIds: null,
    targetBatches: null,
    targetEntries: null,
    targetFiles: null,
    targetSamples: null,
    targetStorables: null,
  };
  if (field.fieldType === FIELD_TYPES.SELECTOR.type) {
    initialTag.schemaFieldSelectorOptionIds = [];
  }
  const linksKey = getLinksKeyFromFieldType(field.fieldType);
  if (linksKey) {
    initialTag[linksKey] = [];
  }
  return initialTag;
}

type GetInitialTagReturnType<T extends SchemaFieldDataType | SchemaField> = T extends SchemaField
  ? SchemaTag
  : UnaffiliatedSchemaTag;

export function getInitialTag<T extends SchemaFieldDataType | SchemaField>(
  field: T
): GetInitialTagReturnType<T> {
  if (isSchemaFieldDatabaseBacked(field)) {
    const initialTag: SchemaTag = {
      tagSchemaField: field,
      name: field.name,
      value: '',
      url: '',
      booleanValue: null,
      datetimeValue: null,
      floatValue: null,
      integerValue: null,
      jsonValue: null,
      schemaFieldSelectorOptionIds: null,
      targetBatches: null,
      targetEntries: null,
      targetFiles: null,
      targetSamples: null,
      targetStorables: null,
      ...(field.isComputed && {
        computedState: {
          status: Status.SUCCEEDED,
          errorMessage: null,
        },
      }),
    };
    if (field.fieldType === FIELD_TYPES.SELECTOR.type) {
      initialTag.schemaFieldSelectorOptionIds = [];
    }
    if (field.fieldType === FIELD_TYPES.FLOAT.type) {
      initialTag.rawValue = '';
    }
    const linksKey = getLinksKeyFromFieldType(field.fieldType);
    if (linksKey) {
      initialTag[linksKey] = [];
    }
    return initialTag;
  } else {
    // The type is correct, and callers will infer "Unaffiliated" correctly
    return getInitialUnaffiliatedTag(field) as GetInitialTagReturnType<T>;
  }
}

export function getInitialTags<T extends SchemaField | SchemaFieldDataType>(
  fields: Array<T>
): Array<ContainingSchemaTagType<T>> {
  return fields.map((field) => getInitialTag(field));
}

export function getFullFieldType(fieldType: FieldType): FullFieldType {
  const fieldTypeKey = _.findKey(FIELD_TYPES, (type) => type.type === fieldType);
  assert(fieldTypeKey != null, `Full field type not found for ${fieldType}`);
  return FIELD_TYPES[fieldTypeKey];
}

export function getLinksKey(fieldType: FieldType): keyof SchemaTag {
  const fullFieldType = getFullFieldType(fieldType);
  const linksKey = getLinksKeyFromFieldType(fullFieldType.type);
  if (!linksKey) {
    throw new Error(`Unexpected fieldType: ${JSON.stringify(fieldType)}`);
  }
  return linksKey;
}

export function maybeGetLinkedItemDisplayName(linkedItem: TargetLinkable): string {
  // This function is currently only used for types that provide a name (bioentities, batches, storables).
  // If it's needed for other types, the return value can be expanded with other properties.
  // eslint-disable-next-line benchlint/no-in-operator
  return 'name' in linkedItem ? linkedItem.name || '' : '';
}

export function tagsAreEqual(tags: Array<Tag>): boolean {
  const uniqueValues = new Set(tags.map((tag) => stringify(getTagUpdateParams(tag))));
  return uniqueValues.size === 1;
}

/**
 * @param fieldType
 * @returns string for the tag key with the relevant value based on the field's type
 */
export function relevantKeyFromFieldType(
  fieldType: FieldType
): Optional<keyof (SchemaTag | UnaffiliatedSchemaTag | MinimalSchemaTag)> {
  const linksKey = getLinksKeyFromFieldType(fieldType);
  switch (fieldType) {
    // TODO(BNCH-12786): use dateValue for date fields
    case FIELD_TYPES.TEXT.type:
    case FIELD_TYPES.DATE.type:
    case FIELD_TYPES.LONG_TEXT.type:
      return 'value';
    case FIELD_TYPES.INTEGER.type:
      return 'integerValue';
    case FIELD_TYPES.FLOAT.type:
      return 'floatValue';
    case FIELD_TYPES.BOOLEAN.type:
      return 'booleanValue';
    case FIELD_TYPES.DATETIME.type:
      return 'datetimeValue';
    case FIELD_TYPES.SELECTOR.type:
      return 'schemaFieldSelectorOptionIds';
    case FIELD_TYPES.JSON.type:
      return 'jsonValue';
    case FIELD_TYPES.AA_PART_LINK.type:
    case FIELD_TYPES.BASE_MOLECULE_LINK.type:
    case FIELD_TYPES.ASSAY_REQUEST_LINK.type:
    case FIELD_TYPES.ASSAY_RESULT_LINK.type:
    case FIELD_TYPES.ASSAY_RUN_LINK.type:
    case FIELD_TYPES.BATCH_LINK.type:
    case FIELD_TYPES.BIOENTITY_LINK.type:
    case FIELD_TYPES.ENTRY_LINK.type:
    case FIELD_TYPES.PART_LINK.type:
    case FIELD_TYPES.TRANSLATION_LINK.type:
    case FIELD_TYPES.TRANSCRIPTION_LINK.type:
    case FIELD_TYPES.BLOB_LINK.type:
    case FIELD_TYPES.SAMPLE_LINK.type:
    case FIELD_TYPES.STORABLE_LINK.type:
      return linksKey;
    default:
      return undefined;
  }
}

/**
 * Return transformed tags adequate for TagTable
 *
 * Currently, only used to transform datetime tags because the server sends datetime values in UTC
 * format but we want to present datetime values in a localized format
 */
export function getTagTableFriendlyTags(tags: Array<SchemaTag>): Array<SchemaTag> {
  return tags.map((tag) => {
    switch (tag.tagSchemaField.fieldType) {
      case FIELD_TYPES.DATETIME.type: {
        return {...tag, value: tag.value ? formalTime(tag.value) : tag.value}; // localize datetime value
      }
      default:
        return tag;
    }
  });
}

/**
 * Returns an update dict for updating a tag.
 *
 * For bioentity and batch links, we allow setting the value to text if no target links are selected.
 */
export function getTagUpdateParams<T extends Tag | UnaffiliatedSchemaTag | MinimalSchemaTag>(
  tag: T
): Partial<
  // We omit tagSchemaField as it allows a Partial<MinimalSchemaTag> to be assigned to a Partial<SchemaTag>.
  Omit<T, 'tagSchemaField'>
> {
  if (!isCustomTag(tag)) {
    const fieldType = tag.tagSchemaField.fieldType;
    const relevantKey = relevantKeyFromFieldType(fieldType) as Optional<keyof T>;
    const maybeLegalTextValues =
      isAffiliatedSchemaTag(tag) && tag.tagSchemaField.legalTextSelectorId
        ? {schemaFieldSelectorOptionIds: tag.schemaFieldSelectorOptionIds}
        : {};

    switch (fieldType) {
      case FIELD_TYPES.LONG_TEXT.type:
      case FIELD_TYPES.INTEGER.type:
      case FIELD_TYPES.FLOAT.type:
      case FIELD_TYPES.BOOLEAN.type:
      case FIELD_TYPES.DATE.type:
      case FIELD_TYPES.DATETIME.type:
      case FIELD_TYPES.JSON.type:
      case FIELD_TYPES.BLOB_LINK.type:
      case FIELD_TYPES.SAMPLE_LINK.type:
      case FIELD_TYPES.STORABLE_LINK.type:
        return {...maybeLegalTextValues, ...pick(tag, relevantKey!)};
      case FIELD_TYPES.SELECTOR.type:
        return pick(tag, relevantKey!);
      case FIELD_TYPES.AA_PART_LINK.type:
      case FIELD_TYPES.BASE_MOLECULE_LINK.type:
      case FIELD_TYPES.ASSAY_REQUEST_LINK.type:
      case FIELD_TYPES.ASSAY_RESULT_LINK.type:
      case FIELD_TYPES.ASSAY_RUN_LINK.type:
      case FIELD_TYPES.BATCH_LINK.type:
      case FIELD_TYPES.BIOENTITY_LINK.type:
      case FIELD_TYPES.ENTRY_LINK.type:
      case FIELD_TYPES.PART_LINK.type:
      case FIELD_TYPES.TRANSCRIPTION_LINK.type:
      case FIELD_TYPES.TRANSLATION_LINK.type:
        if (!tag.value || (relevantKey != null && tagHasLink(tag))) {
          return {...maybeLegalTextValues, ...pick(tag, relevantKey!)};
        } else {
          // @ts-expect-error
          return {...maybeLegalTextValues, ...pick(tag, 'value')};
        }
      default:
    }
  }
  assertAndContinue(
    isCustomTag(tag) || tag.tagSchemaField.fieldType === FIELD_TYPES.TEXT.type,
    `Unexpected field type ${!isCustomTag(tag) ? tag.tagSchemaField.fieldType : ''}`
  );
  // Otherwise, update text value and url for custom tags or text schema tags.
  // @ts-expect-error
  return {
    value: tag.value,
    url: tag.url,
  };
}

function getTagErrorMessage(tag: Tag): string | null {
  const tagUpdateParams = getTagUpdateParams(tag);
  if (tag.value && Object.values(tagUpdateParams).every((val) => isNullValue(val))) {
    return `Field value for "${tag.name}" is invalid`;
  }
  return null;
}

/**
 * Validates the tags and notifies the user if the tags are not valid.
 *
 * @param {Tag[]} tags A list of schema tags to validate
 * @returns {boolean} Returns true if the tags are valid, otherwise false
 */
export function areTagsValidAndNotify(tags: Array<Tag>): boolean {
  const tagErrorMessages = _.compact(tags.map((tag) => getTagErrorMessage(tag)));
  if (tagErrorMessages.length) {
    notifications.error(tagErrorMessages[0], {duration: 0});
    return false;
  }
  return true;
}

/**
 * Transforms tags to objects of {tagName: tagUpdateParams} to send to server for setting tags.
 *
 * NOTE: When used to get the tags for model creation or update, call `areTagsValidAndNotify` before to
 * validate the tags. Otherwise, the invalid tags are silently stripped.
 *
 * @param {Tag} tags A list of tags to transform
 * @returns {TagUpdateObject} A tag update object
 */
export function getTagsToSet(tags: Array<Tag>): TagUpdateObject {
  // @ts-expect-error see https://github.com/benchling/aurelia/pull/12149
  return _.chain(tags)
    .indexBy((tag) => (isSchemaTag(tag) ? tag.tagSchemaField.name : tag.name))
    .mapObject(getTagUpdateParams)
    .value();
}

export function getSortedSchemaAndCustomTags({
  tags,
  tagSchema,
}: {
  tags: Array<Tag>;
  tagSchema: TagSchema | null;
}): {schemaTags: Array<SchemaTag>; customTags: Array<Tag>} {
  // Return all fields of the tag schema, sorted by field position, then all the custom tags, preserving
  // input order.
  if (!tagSchema) {
    return {schemaTags: [], customTags: tags};
  }
  const schemaFieldNameToIndex = new Map<string, number>();
  tagSchema.fields.forEach((field, i) => {
    schemaFieldNameToIndex.set(field.name, i);
  });

  const schemaTags: Array<SchemaTag> = [];
  const customTags: Array<CustomTag> = [];

  for (const tag of tags) {
    if (isCustomTag(tag)) {
      customTags.push(tag);
    } else {
      schemaTags.push(tag);
    }
  }

  const sortedSchemaTags = _.sortBy(schemaTags, (tag) => schemaFieldNameToIndex.get(tag.name));
  return {schemaTags: sortedSchemaTags, customTags};
}

/**
 * This is analogous to SchemaTag.is_null_value on the server.
 */
export function isNullValue(value: unknown): boolean {
  return value == null || value === '' || (Array.isArray(value) && value.length === 0);
}

export function tagHasValue(tag: Tag | null): boolean {
  if (tag == null) {
    return false;
  }
  const tagParams = getTagUpdateParams(tag);
  return tagParams != null && Object.values(tagParams).some((v) => !isNullValue(v));
}

export function tagHasLink(tag: Tag | UnaffiliatedSchemaTag | MinimalSchemaTag): boolean {
  const tagSchemaField: SchemaField | undefined = (tag as SchemaTag).tagSchemaField;
  if (tagSchemaField == null) {
    return false;
  }
  const linksKey = getLinksKeyFromFieldType(tagSchemaField.fieldType);
  if (linksKey == null) {
    assertAndContinue(false, `Linked key not found for ${tagSchemaField.fieldType}`);
    return false;
  }
  const linkedItems = (tag as SchemaTag)[linksKey];
  return linkedItems != null && linkedItems.length > 0;
}

/**
 * @param tags - a list of tags
 * @param fields - fields of a schema. Archived fields will be ignored.
 *
 * @returns boolean indicating whether or not every required field has a value
 */
export function hasValueForAllRequiredFields(tags: Array<SchemaTag>, fields: Array<SchemaField>): boolean {
  return fields.every(
    (field: SchemaField) =>
      !field.isRequired ||
      field.archiveRecord ||
      tags.some((tag) => tag.tagSchemaField.apiId === field.apiId && tagHasValue(tag))
  );
}

export function findSchemaTag(tags: Array<Tag>, fieldId: number): SchemaTag | null {
  return tags.find((tag): tag is SchemaTag => isSchemaTag(tag) && tag.tagSchemaField.id === fieldId) || null;
}

export function findSchemaTagByApiId(tags: Array<Tag>, fieldApiId: string): SchemaTag | null {
  return (
    tags.find((tag): tag is SchemaTag => isSchemaTag(tag) && tag.tagSchemaField.apiId === fieldApiId) || null
  );
}

export function filterToSchemaTags(tags: Array<Tag>): Array<SchemaTag> {
  return tags.filter((tag) => isSchemaTag(tag));
}

export function getTagPlaceholder(tag: SchemaTag | null): string | undefined {
  return tag && !tagHasValue(tag) && IntegrationTagUtil.isIntegrationTag(tag)
    ? 'Acquiring from integration...'
    : undefined;
}

/**
 * Transforms the key-value pairs of the given tagUpdateObj into a Tags Dictionary object.
 *
 * @param {TagUpdateObject} tagUpdateObj the TagUpdateObject to transform.
 * @param {TagsDict} tagsDictRef an optional Tags Dictionary object to modify and return, otherwise a new Tags Dictionary will be returned.
 * @returns {TagsDict} a tags dictionary object
 */
export function tagUpdateObjectToTagsDict(tagUpdateObj: TagUpdateObject, tagsDictRef?: TagsDict): TagsDict {
  const newTagsDict: TagsDict = {};
  Object.entries(tagUpdateObj).forEach((entry) => {
    if (tagsDictRef) {
      tagsDictRef[entry[0]] = entry[1] as Required<Tag>;
    } else {
      newTagsDict[entry[0]] = entry[1] as Required<Tag>;
    }
  });
  if (tagsDictRef) {
    return tagsDictRef;
  }
  return newTagsDict;
}

export function isConvertedFromTag(tag: SchemaTag): boolean {
  return Boolean(tag.tagSchemaField?.isConvertedFromLink);
}
